import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSeo } from '@/lib/seo';
import { useJsonLd } from '@/lib/json-ld';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { LogoIcon } from '@/components/ui/Logo';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { IconBadge } from '@/components/ui/icon-badge';
import { PlanFeatureRow } from '@/components/ui/plan-feature-row';
import { BillingCheckoutDialog, type BillingSku } from '@/components/billing';
import { purchasesAllowed } from '@/lib/purchases';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  Check,
  X,
  Sparkles,
  Zap,
  Crown,
  ArrowRight,
  ChevronLeft,
  Flame,
} from 'lucide-react';
import {
  PUBLIC_PLANS,
  STORAGE_PACKS,
  formatPriceBRL,
  formatStorage,
  computeDiscountPercent,
  type PlanDefinition,
  type PlanSlug,
} from '@my360/shared';
import type { BillingInfo } from '@my360/shared';
import { useLoadingGate } from '@/store/loadingStore';

const PLAN_ICON: Record<PlanSlug, React.ElementType> = {
  free: Sparkles,
  pro: Zap,
  enterprise: Crown,
  beta: Sparkles,
};

type TFn = import('i18next').TFunction;

function buildComparisonRows(t: TFn): Array<{
  label: string;
  /** Pulls from limits + extra computed cells */
  render: (p: PlanDefinition) => React.ReactNode;
}> {
  return [
    { label: t('pricingPage:rows.tours'), render: (p) => (p.limits.maxProjects === -1 ? t('pricingPage:cell.unlimited') : `${p.limits.maxProjects}`) },
    { label: t('pricingPage:rows.storage'), render: (p) => formatStorage(p.limits.maxStorage) },
    { label: t('pricingPage:rows.noBrand'), render: (p) => (p.limits.whiteLabel ? <CheckIcon /> : <DashIcon />) },
    { label: t('pricingPage:rows.customDomain'), render: (p) => (p.limits.customDomain ? <CheckIcon /> : <DashIcon />) },
    { label: t('pricingPage:rows.advancedAnalytics'), render: (p) => (p.limits.analytics ? <CheckIcon /> : <DashIcon />) },
    { label: t('pricingPage:rows.passwordProtect'), render: (p) => (p.limits.passwordProtect ? <CheckIcon /> : <DashIcon />) },
    { label: t('pricingPage:rows.leadCapture'), render: (p) => (p.limits.leadCapture ? <CheckIcon /> : <DashIcon />) },
  ];
}

function CheckIcon() {
  return <Check className="w-4 h-4 text-[var(--color-accent)] mx-auto" strokeWidth={2.5} />;
}
function DashIcon() {
  return <X className="w-4 h-4 text-[var(--color-text-muted)] mx-auto opacity-40" />;
}

// ── Plan card ───────────────────────────────────────────────────────
interface PlanCardProps {
  plan: PlanDefinition;
  currentPlan: string;
  isAuthenticated: boolean;
  index: number;
  onSelect: (slug: string) => void;
}

function PlanCard({ plan, currentPlan, isAuthenticated, index, onSelect }: PlanCardProps) {
  const { t } = useTranslation();
  const Icon = PLAN_ICON[plan.slug] || Zap;
  const isCurrent = plan.slug === currentPlan;
  const isHighlighted = plan.slug === 'pro';
  const discount = computeDiscountPercent(plan);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
      className={cn(
        'relative p-7 rounded-2xl border flex flex-col',
        isHighlighted
          ? 'border-[var(--color-primary)] bg-gradient-to-b from-[var(--color-primary)]/8 via-[var(--color-surface)] to-[var(--color-surface)] shadow-[0_0_60px_-20px_rgba(79,70,229,0.5)]'
          : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-primary)]/40 transition-colors',
      )}
    >
      {/* Top badges */}
      {isHighlighted && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -mt-3 px-3 py-1 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-lg flex items-center gap-1">
          <Flame className="w-3 h-3" />
          {t('pricingPage:card.mostPopular')}
        </div>
      )}
      {discount && (
        <div className="absolute top-4 right-4 px-2 py-0.5 bg-[var(--color-danger)]/15 border border-[var(--color-danger)]/40 text-[var(--color-danger)] text-[10px] font-bold uppercase tracking-wider rounded-full">
          {t('pricingPage:card.discountBadge', { pct: discount })}
        </div>
      )}
      {isCurrent && (
        <div className="absolute top-4 left-4 px-2 py-0.5 bg-[var(--color-accent)]/15 border border-[var(--color-accent)]/40 text-[var(--color-accent)] text-[10px] font-bold uppercase tracking-wider rounded-full">
          {t('pricingPage:card.currentPlan')}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-1.5 mt-2">
        <IconBadge icon={Icon} variant="primary" size="md" />
        <h3 className="text-2xl font-semibold tracking-tight">{plan.name}</h3>
      </div>
      <p className="text-sm text-[var(--color-text-muted)] mb-7 min-h-[40px]">{plan.tagline}</p>

      {/* Price block */}
      <div className="mb-7 text-[var(--color-text)]">
        {plan.displayFromCents && (
          <div className="text-sm text-[var(--color-text-muted)] line-through mb-1">
            {formatPriceBRL(plan.displayFromCents)}
          </div>
        )}
        <div className="text-5xl font-display font-bold tracking-tighter leading-none flex items-baseline">
          {plan.priceCents === 0 ? (
            'R$ 0'
          ) : (
            <>
              {formatPriceBRL(plan.priceCents)}
              <span className="text-base font-normal ml-1.5 text-[var(--color-text-muted)]">{t('pricingPage:card.perMonth')}</span>
            </>
          )}
        </div>
        <div className="text-xs mt-2 text-[var(--color-text-muted)]">
          {plan.priceCents === 0
            ? t('pricingPage:card.freeForever')
            : t('pricingPage:card.billedMonthly')}
        </div>
      </div>

      {/* CTA */}
      <Button
        variant={isHighlighted ? 'primary' : isCurrent ? 'outline' : 'glass'}
        className="w-full h-12 rounded-xl mb-7"
        disabled={isCurrent}
        onClick={() => onSelect(plan.slug)}
      >
        {isCurrent ? (
          t('pricingPage:card.currentPlan')
        ) : isAuthenticated ? (
          <>
            {plan.slug === 'free' ? t('pricingPage:card.backToFree') : t('pricingPage:card.upgrade')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        ) : (
          <>
            {plan.slug === 'free' ? t('pricingPage:card.startFree') : t('pricingPage:card.subscribe', { name: plan.name })}
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>

      {/* Feature list */}
      <div className="space-y-2.5 flex-1">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-text-muted)] mb-3">
          {t('pricingPage:card.includes')}
        </div>
        {plan.features.map((f, i) => (
          <PlanFeatureRow
            key={i}
            included={f.included}
            emphasize={f.emphasize}
            tone={isHighlighted ? 'primary' : 'accent'}
          >
            {f.label}
          </PlanFeatureRow>
        ))}
      </div>
    </motion.div>
  );
}

// ── Page ────────────────────────────────────────────────────────────
export default function Pricing() {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  useSeo({ title: t('seo:pricing.title'), description: t('seo:pricing.description'), canonicalPath: '/pricing' });
  // Preço no resultado de busca: o Google mostra faixa de valores e "a
  // partir de" quando a oferta vem marcada. Os valores saem de PUBLIC_PLANS
  // (@my360/shared) — a mesma fonte que renderiza os cards, então preço
  // marcado e preço exibido não têm como divergir.
  useJsonLd('pricing-offers', {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': 'https://my360.site/pricing#product',
    name: 'my360',
    description: t('seo:pricing.description'),
    brand: { '@type': 'Brand', name: 'my360' },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'BRL',
      lowPrice: '0',
      highPrice: (Math.max(...PUBLIC_PLANS.map((p) => p.priceCents)) / 100).toFixed(2),
      offerCount: String(PUBLIC_PLANS.length),
      availability: 'https://schema.org/InStock',
      url: 'https://my360.site/pricing',
    },
  });
  const COMPARISON_ROWS = useMemo(() => buildComparisonRows(t), [t]);
  const { isAuthenticated, tenant, checkAuth } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutSku, setCheckoutSku] = useState<BillingSku | null>(null);

  // App nativo (Google Play): /pricing é uma vitrine de venda inteira —
  // preços + checkout Asaas — e não pode existir dentro do app (Play
  // Billing policy). Redireciona pra visão de conta.
  const canBuy = purchasesAllowed();
  useEffect(() => {
    if (!canBuy) {
      navigate({ to: isAuthenticated ? '/billing' : '/login', replace: true });
    }
  }, [canBuy, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isAuthenticated) {
          // Touch the billing endpoint to ensure tenant info is fresh; data not retained
          await api.request<{ data: BillingInfo }>('/billing');
        }
      } catch (err) {
        console.error('Failed to fetch billing context:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated]);

  const handleSelectPlan = (slug: string) => {
    if (!isAuthenticated) {
      navigate({ to: '/register' });
      return;
    }
    if (slug === tenant?.plan) return;
    if (slug === 'free') {
      toast.info(t('pricingPage:toastFreeDowngrade'));
      return;
    }
    const plan = visiblePlans.find((p) => p.slug === slug);
    if (!plan) return;
    setCheckoutSku({
      kind: 'plan',
      slug: plan.slug,
      name: plan.name,
      priceCents: plan.priceCents,
      tagline: plan.tagline,
    });
  };

  // Atenção: onSuccess é disparado quando o checkout RESPONDE (PIX gerado /
  // boleto criado / cartão tokenizado), NÃO quando o pagamento confirma.
  // O toast de "Pagamento confirmado!" sai do useBillingSocket → onPaymentConfirmed,
  // disparado quando o webhook real do Asaas chega. Aqui só refresca auth.
  const handleCheckoutSuccess = () => {
    checkAuth();
  };

  const currentPlan = tenant?.plan || 'free';
  const visiblePlans = useMemo(() => PUBLIC_PLANS, []);

  // A espera vive no gate global — nunca fecha um loader pra abrir outro.
  useLoadingGate('pricing', isLoading && isAuthenticated);

  // Evita o flash da vitrine antes do redirect nativo acima.
  if (!canBuy) return null;

  if (isLoading && isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--color-base)] text-[var(--color-text)] overflow-y-auto">
      {/* ── Top nav ── */}
      <nav className="fixed top-0 inset-x-0 z-50 px-6 py-4 flex items-center justify-between glass-panel border-x-0 border-t-0">
        <button onClick={() => navigate({ to: '/' })} className="flex items-center gap-1.5">
          <LogoIcon size="lg" />
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <span className="font-logo text-2xl leading-none">my360</span>
        </button>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          {isAuthenticated ? (
            <Button variant="ghost" onClick={() => navigate({ to: '/dashboard' })}>
              <ChevronLeft className="w-4 h-4 mr-1" /> {t('pricingPage:nav.dashboard')}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate({ to: '/login' })}>
                {t('pricingPage:nav.signIn')}
              </Button>
              <Button variant="primary" onClick={() => navigate({ to: '/register' })}>
                {t('pricingPage:nav.startFree')}
              </Button>
            </>
          )}
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative pt-32 pb-16 overflow-hidden border-b border-[var(--color-border)]">
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-primary)]/10 to-transparent pointer-events-none" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[var(--color-primary)]/20 blur-[140px] opacity-50 pointer-events-none" />
        <div className="text-center relative z-10 max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 mb-6 px-3 py-1 rounded-full border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-primary)]"
          >
            <Flame className="w-3 h-3" />
            {t('pricingPage:hero.badge')}
          </motion.div>
          <h1 className="text-display text-5xl md:text-6xl font-semibold tracking-tighter mb-5 leading-[1.05]">
            {t('pricingPage:hero.title')}
          </h1>
          <p className="text-xl text-[var(--color-text-muted)] font-light max-w-xl mx-auto">
            {t('pricingPage:hero.sub')}
          </p>
        </div>
      </section>

      {/* ── Plan cards ──
          Mobile: scroll horizontal com snap (cada card 85vw + peek do
          próximo) — vertical empilhado faria a página gigante e mata
          conversão antes do user chegar na comparação.
          Desktop: grid 3 colunas como antes. */}
      <section className="max-w-6xl mx-auto py-12 md:py-16">
        <div
          className={cn(
            // mobile: horizontal snap scroll
            'flex md:hidden gap-4 overflow-x-auto snap-x snap-mandatory px-6 pb-4',
            'hide-scrollbar',
          )}
        >
          {visiblePlans.map((p, i) => (
            <div
              key={p.slug}
              className="snap-center shrink-0 w-[85vw] max-w-[360px]"
            >
              <PlanCard
                plan={p}
                currentPlan={currentPlan}
                isAuthenticated={isAuthenticated}
                index={i}
                onSelect={handleSelectPlan}
              />
            </div>
          ))}
        </div>

        {/* Dots indicator mobile (visual cue de que é swipeable) */}
        <div className="flex md:hidden justify-center gap-1.5 mt-2">
          {visiblePlans.map((p) => (
            <span
              key={p.slug}
              className="h-1 w-6 rounded-full bg-[var(--color-border)]"
              aria-hidden
            />
          ))}
        </div>
        <p className="md:hidden text-center text-[11px] text-[var(--color-text-muted)] mt-2">
          {t('pricingPage:swipeHint')}
        </p>

        <div className="hidden md:grid md:grid-cols-3 gap-6 px-6">
          {visiblePlans.map((p, i) => (
            <PlanCard
              key={p.slug}
              plan={p}
              currentPlan={currentPlan}
              isAuthenticated={isAuthenticated}
              index={i}
              onSelect={handleSelectPlan}
            />
          ))}
        </div>
      </section>

      {/* ── Comparison table ── */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <h3 className="text-2xl font-semibold tracking-tight mb-2 text-center">
          {t('pricingPage:compare.heading')}
        </h3>
        <p className="text-sm text-[var(--color-text-muted)] text-center mb-10">
          {t('pricingPage:compare.sub')}
        </p>
        <div className="rounded-2xl border border-[var(--color-border)] overflow-hidden bg-[var(--color-surface)]">
          {/* Header */}
          <div className={cn('grid gap-4 p-4 bg-[var(--color-surface-elevated)] border-b border-[var(--color-border)]', `grid-cols-${visiblePlans.length + 2}`)} style={{ gridTemplateColumns: `1.6fr repeat(${visiblePlans.length}, 1fr)` }}>
            <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-text-muted)]">
              {t('pricingPage:compare.feature')}
            </div>
            {visiblePlans.map((p) => (
              <div
                key={p.slug}
                className={cn(
                  'text-xs font-semibold uppercase tracking-widest text-center',
                  p.slug === 'pro' && 'text-[var(--color-primary)]',
                )}
              >
                {p.name}
              </div>
            ))}
          </div>
          {/* Rows */}
          {COMPARISON_ROWS.map((row) => (
            <div
              key={row.label}
              className="grid gap-4 p-4 border-b border-[var(--color-border)] last:border-b-0 items-center hover:bg-[var(--color-surface-hover)] transition-colors"
              style={{ gridTemplateColumns: `1.6fr repeat(${visiblePlans.length}, 1fr)` }}
            >
              <div className="text-sm text-[var(--color-text)]">{row.label}</div>
              {visiblePlans.map((p) => (
                <div
                  key={p.slug}
                  className={cn(
                    'text-sm text-center',
                    typeof row.render(p) === 'string' ? 'font-mono text-[var(--color-text)]' : '',
                  )}
                >
                  {row.render(p)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ── Storage packs strip ──
          IconBadge removido — em mobile ele comia ~60px da largura e
          deixava os cards de pack apertados/quebrados. Visual fica mais
          limpo direto. */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 pb-16 md:pb-20">
        <div className="rounded-2xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-accent)]/5 via-[var(--color-surface)] to-[var(--color-primary)]/5 p-5 md:p-8">
          <h3
            className="text-lg font-semibold tracking-tight mb-1"
            style={{ color: 'var(--color-text)' }}
          >
            {t('pricingPage:packs.heading')}
          </h3>
          <p className="text-sm text-[var(--color-text-muted)] mb-5">
            {t('pricingPage:packs.sub')}
          </p>
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {/* Da mesma fonte que a cobrança. Escritos à mão aqui, ficavam
                desatualizados no primeiro ajuste de preço — foi assim que a
                landing anunciou R$ 89 enquanto o cartão era debitado em
                R$ 89,90. */}
            {STORAGE_PACKS.map((pack) => {
              const p = {
                gb: Math.round(pack.bonusBytes / 1024 ** 3),
                price: formatPriceBRL(pack.priceCents),
              };
              return (
              <div
                key={pack.slug}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-center"
              >
                <div
                  className="text-lg md:text-xl font-bold tabular-nums"
                  style={{ color: 'var(--color-text)' }}
                >
                  {t('pricingPage:storagePack.gbLabel', { gb: p.gb })}
                </div>
                <div className="text-xs mt-0.5">
                  <span
                    className="font-semibold"
                    style={{ color: 'var(--color-text)' }}
                  >
                    {p.price}
                  </span>
                  <span className="text-[var(--color-text-muted)]">{t('pricingPage:card.perMonth')}</span>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Footer CTA ── */}
      <section className="border-t border-[var(--color-border)] bg-[var(--color-surface)] py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl font-semibold tracking-tight mb-3">{t('pricingPage:footer.heading')}</h3>
          <p className="text-[var(--color-text-muted)] mb-6">
            {t('pricingPage:footer.sub')}
          </p>
          <a href="mailto:hello@my360.site">
            <Button variant="primary">
              {t('pricingPage:footer.contact')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
        </div>
      </section>

      <BillingCheckoutDialog
        open={!!checkoutSku}
        onOpenChange={(o) => !o && setCheckoutSku(null)}
        sku={checkoutSku}
        onSuccess={handleCheckoutSuccess}
      />
    </div>
  );
}
